/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-04
 */

// Add third-party dependencies.
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import {
  TIMEZONE_OPTIONS,
  TimezoneOption,
} from '../../../utils/studioSettings';
import {
  useUpdateProfileMutation,
  UpdateProfileRequest,
} from '../../../features/auth';
import { sanitizeErrorMessage } from '../../../utils/security';

/**
 * Interface for user account preferences data.
 */
interface AccountPreferencesData {
  marketingEmails: boolean;
  timezone: string;
}

/**
 * Props for the AccountPreferencesSection component.
 */
interface AccountPreferencesSectionProps {
  /** Initial user data */
  initialData: AccountPreferencesData;
  /** Whether the section is disabled */
  disabled?: boolean;
  /** Callback for successful updates */
  onUpdate?: (message: string) => void;
  /** Callback for errors */
  onError?: (error: string) => void;
}

/**
 * Account preferences section component for the Settings page.
 * Handles marketing email preferences and timezone selection.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The account preferences section component
 *
 * @example
 * return (
 *   <AccountPreferencesSection
 *     initialData={{
 *       marketingEmails: true,
 *       timezone: 'America/Los_Angeles'
 *     }}
 *     onUpdate={(message) => setSuccessMessage(message)}
 *     onError={(error) => setErrorMessage(error)}
 *   />
 * );
 */
const AccountPreferencesSection: React.FC<AccountPreferencesSectionProps> = ({
  initialData,
  disabled = false,
  onUpdate,
  onError,
}) => {
  const { t } = useTranslation('settings');
  const [formData, setFormData] = useState<AccountPreferencesData>(initialData);
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

  // Update form data when initial data changes
  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  /**
   * Handles marketing email toggle change.
   */
  const handleMarketingEmailsChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.checked;

    try {
      const updateData: Partial<UpdateProfileRequest> = {
        marketing_emails: newValue,
      };
      await updateProfile(updateData as UpdateProfileRequest).unwrap();

      setFormData(prev => ({ ...prev, marketingEmails: newValue }));

      if (onUpdate) {
        onUpdate(
          newValue
            ? t('accountPreferences.marketingEnabledSuccess')
            : t('accountPreferences.marketingDisabledSuccess')
        );
      }
    } catch (error) {
      if (onError) {
        onError(sanitizeErrorMessage(error));
      }
      // Note: Visual revert will happen automatically through form state
    }
  };

  /**
   * Handles timezone selection change.
   */
  const handleTimezoneChange = async (
    _: React.SyntheticEvent,
    newValue: TimezoneOption | null
  ) => {
    if (!newValue) return;

    const newTimezone = newValue.value;

    try {
      const updateData: Partial<UpdateProfileRequest> = {
        timezone: newTimezone,
      };
      await updateProfile(updateData as UpdateProfileRequest).unwrap();

      setFormData(prev => ({ ...prev, timezone: newTimezone }));

      if (onUpdate) {
        onUpdate(
          t('accountPreferences.timezoneUpdated', { timezone: newValue.label })
        );
      }
    } catch (error) {
      if (onError) {
        onError(sanitizeErrorMessage(error));
      }
    }
  };

  // Find current timezone option for display
  const currentTimezoneOption =
    TIMEZONE_OPTIONS.find(
      (option: TimezoneOption) => option.value === formData.timezone
    ) || null;

  return (
    <Box>
      {/* Section Header */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {t('accountPreferences.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('accountPreferences.description')}
      </Typography>

      {/* Marketing Emails */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <EmailIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {t('accountPreferences.marketingCommunications')}
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={formData.marketingEmails}
              onChange={handleMarketingEmailsChange}
              disabled={disabled || isUpdating}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body2">
                {t('accountPreferences.marketingEmailsLabel')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('accountPreferences.marketingEmailsHelper')}
              </Typography>
            </Box>
          }
          sx={{ ml: 0, alignItems: 'flex-start' }}
        />
      </Box>

      {/* Timezone Selection */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ScheduleIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {t('accountPreferences.timezone')}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('accountPreferences.timezoneDescription')}
        </Typography>

        <Autocomplete
          value={currentTimezoneOption}
          onChange={handleTimezoneChange}
          options={TIMEZONE_OPTIONS}
          getOptionLabel={(option: TimezoneOption) => option.label}
          isOptionEqualToValue={(
            option: TimezoneOption,
            value: TimezoneOption
          ) => option.value === value.value}
          disabled={disabled || isUpdating}
          loading={isUpdating}
          renderInput={params => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              label={t('accountPreferences.timezoneLabel')}
              placeholder={t('accountPreferences.timezonePlaceholder')}
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isUpdating ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
          )}
          renderOption={(props, option: TimezoneOption) => (
            <Box
              component="li"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.value}
                </Typography>
              </Box>
            </Box>
          )}
          filterOptions={(options, { inputValue }) => {
            const filtered = options.filter(
              (option: TimezoneOption) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.value.toLowerCase().includes(inputValue.toLowerCase())
            );
            return filtered.slice(0, 50); // Limit results for performance
          }}
          sx={{ maxWidth: 400 }}
        />
      </Box>
    </Box>
  );
};

export default AccountPreferencesSection;
